/**
 * A situação do compromisso na tela (spec 039): cor, ícone e rótulo. A REGRA
 * (feito > ticket finalizado > ticket cancelado > atrasado > hoje > agendado)
 * é a `commitmentSituation` do @erp/shared — aqui só a aparência de cada uma.
 */
import {
  CalendarClock,
  CheckCheck,
  CheckCircle2,
  Clock,
  TriangleAlert,
  XCircle,
  type LucideIcon,
} from 'lucide-react';
import {
  COMMITMENT_SITUATION_LABELS,
  commitmentSituation,
  type Commitment,
  type CommitmentSituation,
} from '@erp/shared';
import { Badge } from '@/components/ui/misc';
import { cn } from '@/lib/utils';

type BadgeVariant = 'success' | 'default' | 'destructive' | 'warning' | 'info';

export const SITUATION_STYLE: Record<
  CommitmentSituation,
  { icon: LucideIcon; color: string; badge: BadgeVariant }
> = {
  done: { icon: CheckCircle2, color: '#52C41A', badge: 'success' },
  // concluído pelo ticket: o mesmo "acabou", com outro ícone e outro rótulo
  ticket_closed: { icon: CheckCheck, color: '#08979C', badge: 'success' },
  ticket_canceled: { icon: XCircle, color: '#8C8C8C', badge: 'default' },
  overdue: { icon: TriangleAlert, color: '#F5222D', badge: 'destructive' },
  today: { icon: Clock, color: '#FA8C16', badge: 'warning' },
  scheduled: { icon: CalendarClock, color: '#1677FF', badge: 'info' },
};

/** A situação AGORA — `now` é o instante; o dia de São Paulo sai dele lá dentro. */
export function situationOf(c: Commitment, now: Date = new Date()): CommitmentSituation {
  return commitmentSituation(c, now);
}

/** O rótulo do evento na aba de placas: a placa, ou o título do ticket sem ela. */
export function commitmentLabel(c: Commitment): string {
  return c.ticketPlate ?? c.ticketTitle;
}

export function SituationIcon({
  situation,
  className,
}: {
  situation: CommitmentSituation;
  className?: string;
}) {
  const { icon: Icon, color } = SITUATION_STYLE[situation];
  const label = COMMITMENT_SITUATION_LABELS[situation];
  return (
    <Icon
      className={cn('shrink-0', className)}
      style={{ color }}
      role="img"
      aria-label={label}
    >
      <title>{label}</title>
    </Icon>
  );
}

export function CommitmentSituationBadge({ situation }: { situation: CommitmentSituation }) {
  const { icon: Icon, badge } = SITUATION_STYLE[situation];
  return (
    <Badge variant={badge}>
      <Icon className="size-3" aria-hidden />
      {COMMITMENT_SITUATION_LABELS[situation]}
    </Badge>
  );
}
